/// <amd-module name="Build/SourceProviders/BaseGit" />
import { IBuildRestClient } from "Build/Client/RestClient/Build";
import { Base } from "Build/SourceProviders/Base";
import { IPullrequestData, IPullrequestInfo } from "Build/SourceProviders/Common";
import { ShortenedCommitHashLength } from "Build/SourceProviders/Constants";
import { startsWith } from "VSS/Core/Util/String";


export abstract class BaseGit extends Base {
    public abstract getKey(): string;

    public getRepoIconName(): string {
        return "ExternalGit";
    }
    
    public getBranchText(branch: string): string {
        const branchName = this._normalizeBranchName(branch);

        if (startsWith(branchName, "refs/pull/")) {
            const pullRequestId = this._getPullRequestId(branchName);
            if (pullRequestId) {
                return pullRequestId.toString();
            }
        }

        return branchName;
    }

    public getBranchIconName(branch: string): string {
        if (startsWith(branch, "refs/pull/") || startsWith(branch, "refs/heads/refs/pull/")) {
            return "BranchPullRequest";
        }
        else if(startsWith(branch, "refs/tags")) {
            return "Tag";
        }
        else {
            return super.getBranchIconName(branch);
        }
    }

    public getCommitText(commit: string): string {
        if (commit) {
            return commit.slice(0, ShortenedCommitHashLength);
        }
        else {
            return "";
        }
    }

    public fetchPullrequestData(info: IPullrequestInfo): Promise<IPullrequestData | null> {
        if (info.pullRequestNumber) {
            return this._queryPullRequest(info, info.pullRequestNumber);
        }

        // Attempt to extract the pull request number from the source branch if not in triggerInfo
        const pullRequestId: number | undefined = this._getPullRequestId(info.branchName);
        if (pullRequestId !== undefined) {
            return this._queryPullRequest(info, pullRequestId.toString());
        }

        return Promise.resolve(null);
    }

    protected _getPullRequestId(branchName: string): number | undefined {
        branchName = this._normalizeBranchName(branchName || "");
        if (branchName.indexOf("refs/pull/") === 0) {
            const prSourceBranch = branchName.substring("refs/pull/".length);
            const tokens = prSourceBranch.trim().split("/", 2);
            if (tokens.length !== 2) {
                return;
            }
            const mergeString = tokens[1].trim();
            if (mergeString.toLowerCase() !== "merge") {
                return;
            }
            const id = parseInt(tokens[0].trim());
            if (!isNaN(id)) {
                return id;
            }
        }

        return;
    }

    protected _normalizeBranchName(branchName: string): string {
        if (startsWith(branchName, "refs/heads/")) {
            return branchName.substring("refs/heads/".length);
        }
        if (startsWith(branchName, "refs/tags/")) {
            return branchName.substring("refs/tags/".length);
        }
        return branchName;
    }

    protected _queryPullRequest(info: IPullrequestInfo, pullRequestNumber: string): Promise<IPullrequestData> {
        const buildClient: IBuildRestClient = this._getBuildClient();
        return buildClient.getPullRequest(
            info.projectId, this.getKey(), pullRequestNumber, info.repositoryName, info.serviceEndpointId)
            .then(pullRequest => {
                return {
                    id: parseInt(pullRequest.id, 10),
                    title: pullRequest.title,
                    sourceRepositoryOwner: pullRequest.sourceRepositoryOwner,
                    sourceBranch: this.getBranchText(pullRequest.sourceBranchRef),
                    targetRepositoryOwner: pullRequest.targetRepositoryOwner,
                    targetBranch: this.getBranchText(pullRequest.targetBranchRef),
                    rawSourceBranch: pullRequest.sourceBranchRef,
                    rawTargetBranch: pullRequest.targetBranchRef,
                    url: pullRequest._links.web.href
                } as IPullrequestData;
            });
    }
}
